import {
	getEventTypesPublicGetEventTypeAvailabilitiesQueryOptions,
	type EventTypesPublicGetEventTypeAvailabilitiesParams,
	useEventTypesPublicGetEventTypeAvailabilities,
} from '@cocoonspace/api'
import { useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'

interface UseCachedAvailabilitiesOptions {
	staleTime?: number
	gcTime?: number
}

const DEFAULT_STALE_TIME = 1000 * 60 * 5 // 5 minutes
const DEFAULT_GC_TIME = 1000 * 60 * 10 // 10 minutes

/**
 * Hook to fetch event type availabilities with caching and prefetching.
 * Automatically prefetches the next month's data when the current month changes.
 */
export const useCachedEventTypeAvailabilities = (
	eventTypeId: string,
	params: EventTypesPublicGetEventTypeAvailabilitiesParams,
	options: UseCachedAvailabilitiesOptions = {},
) => {
	const queryClient = useQueryClient()
	const { staleTime = DEFAULT_STALE_TIME, gcTime = DEFAULT_GC_TIME } = options

	const month = Number(params.month)
	const year = Number(params.year)

	useEffect(
		function prefetchNextMonth() {
			const nextMonth = month === 12 ? 1 : month + 1
			const nextYear = month === 12 ? year + 1 : year

			void queryClient.prefetchQuery(
				getEventTypesPublicGetEventTypeAvailabilitiesQueryOptions(eventTypeId, {
					month: nextMonth.toString(),
					year: nextYear.toString(),
				}),
			)
		},
		[queryClient, eventTypeId, month, year],
	)

	return useEventTypesPublicGetEventTypeAvailabilities(eventTypeId, params, {
		query: {
			staleTime,
			gcTime,
		},
	})
}
